import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Formik } from "formik";
import { getCaptchaSiteKey, newFieldProps, showCaptchaAtLogin, useCaptcha } from "@awe-web/shared/lib";
import { Translation as T, useTranslation } from "@awe-web/shared/lib";
import { getLoginErrorMessage } from "@awe-web/shared/lib";
import {
  InputLabel,
  Button,
  TextInput,
  PasswordInput,
  RequiredNotice,
  ErrorMessage,
  ScrollToError
} from "../../components/form.js";

import "./login_form.scss";
import HCaptcha from '@hcaptcha/react-hcaptcha';

const LABEL_USERNAME = "FSD0000088";
const LABEL_PASSWORD = "FSD0000090";
const PLACEHOLDER_USERNAME = "FSD0000089";
const PLACEHOLDER_PASSWORD = "FSD0000091";
const BUTTON_LOGIN = "FSD0000092";
const NOTICE = "FSD0000209";
const ERROR_ENTER_USERNAME = "FSD0000256";
const ERROR_ENTER_PASSWORD = "FSD0000257";

export const validate = translate => values => {
  const errors = {};

  if (!values.username) {
    errors.username = translate(ERROR_ENTER_USERNAME);
  }
  if (!values.password) {
    errors.password = translate(ERROR_ENTER_PASSWORD);
  }

  return errors;
};

function InternalLoginForm(props) {
  const fieldProps = newFieldProps(props);
  const error = name => fieldProps(name).error;
  const { onCaptchaChange } = props;

  const { translate } = useTranslation();
  const loginErrorMessage = useSelector((state) => getLoginErrorMessage(state));

  const { captchaRef, handleCaptcha, currentLanguage } = useCaptcha();
  const captchaPublicSiteKey = useSelector((state) => getCaptchaSiteKey(state));
  const showCaptcha = useSelector((state) => showCaptchaAtLogin(state));

  const handleCaptchaChange = useCallback((token) => {
    onCaptchaChange(token);
    handleCaptcha(token);
  }, [handleCaptcha, onCaptchaChange]);

  return (
    <form
      className="login-form"
      onSubmit={props.handleSubmit}
    >
      <ScrollToError />
      <div className="login-form__fields">
        <div className="labeled-field login-form__labeled-field">
          <InputLabel className="labeled-field__input-label" required error={error("username") || loginErrorMessage}>
            <T code={LABEL_USERNAME} />
          </InputLabel>
          <TextInput
            {...fieldProps("username")}
            error={error("username") || loginErrorMessage}
            placeholder={translate(PLACEHOLDER_USERNAME)} />
          <ErrorMessage error={error("username")} />
        </div>

        <div className="labeled-field login-form__labeled-field">
          <InputLabel className="labeled-field__input-label" required error={error("password") || loginErrorMessage}>
            <T code={LABEL_PASSWORD} />
          </InputLabel>
          <PasswordInput
            {...fieldProps("password")}
            error={error("password") || loginErrorMessage}
            placeholder={translate(PLACEHOLDER_PASSWORD)} />
          <ErrorMessage error={error("password")} />
        </div>

        <ErrorMessage
          className="login-form__login-error-message"
          error={loginErrorMessage}
        />

        {showCaptcha && captchaPublicSiteKey && <div className="login-form__captcha_field">
          <HCaptcha
            ref={captchaRef}
            sitekey={captchaPublicSiteKey}
            onVerify={handleCaptchaChange}
            hl={currentLanguage}
          />
        </div>
        }
      </div>

      <Button
        submit
        continue
        primary
        className="login-form__submit-button"
      >
        <T code={BUTTON_LOGIN} />
      </Button>

      <RequiredNotice className="login-form__required-notice">
        <T code={NOTICE} />
      </RequiredNotice>
    </form>
  );
}

InternalLoginForm.propTypes = {
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleSubmit: PropTypes.func,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func
};

export default function LoginForm({ rentContractId, onSubmit, onCaptchaChange }) {
  const { translate } = useTranslation();

  const initialValues = {
    rentContractId,
    username: "",
    password: ""
  };

  return (
    <Formik
      validate={validate(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {(formikProps) => (
        <InternalLoginForm {...formikProps} onCaptchaChange={onCaptchaChange} />
      )}
    </Formik>
  );
}

LoginForm.propTypes = {
  rentContractId: PropTypes.number,
  onSubmit: PropTypes.func
};
